import type { RequiredGeneric } from '@pba/utils';
import { type AppLanguageEntry, getSupportedAppLanguages } from 'i18n/config';
import type { LabelInValue } from 'models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckboxInput, Col, Form, Row, Select, Title } from 'ui';
import { languages } from 'common/utils/languages';

export type UserTranslationsFormValues =
  | { enableTranslations: false; language: string }
  | { enableTranslations: true; language: string; translationLanguage: LabelInValue };

const getTransformedLanguages = (appLanguages: AppLanguageEntry[]) =>
  appLanguages.map((lang) => ({
    label: lang.label.charAt(0).toUpperCase() + lang.label.slice(1),
    value: lang.code,
  }));

const UserTranslationsFormContent = <T extends UserTranslationsFormValues = never>(_: RequiredGeneric<T>) => {
  const { t: translationLanguage } = useTranslation('translationLanguages');
  const transformedLanguage = getTransformedLanguages(getSupportedAppLanguages());

  const languagesForTranslations = useMemo(
    () =>
      languages.map((language) => ({
        label: translationLanguage(language.translationKey),
        value: language.code,
      })),
    [translationLanguage],
  );

  return (
    <Row>
      <Col span={24}>
        <Title level={3} marginBottom>
          App Language
        </Title>
      </Col>
      <Col span={24}>
        <Form.Item<UserTranslationsFormValues>
          extra="The selected language determines the application’s interface language"
          label="Select Language"
          name="language"
          rules={[{ required: true }]}
        >
          <Select options={transformedLanguage} />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Title level={3} marginBottom>
          Translations
        </Title>
      </Col>
      <Col span={24}>
        <CheckboxInput
          item={{
            label: 'Enable translations',
            name: 'enableTranslations',
          }}
        />
      </Col>
      <Col span={24}>
        <Form.Item<UserTranslationsFormValues>
          dependencies={['enableTranslations']}
          extra="The selected language will be used to translate posts, polls, and other content"
          noStyle
        >
          {({ getFieldsValue }) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            const { enableTranslations: isEnabled } = getFieldsValue(true) as Partial<UserTranslationsFormValues>;

            return isEnabled ? (
              <Form.Item<UserTranslationsFormValues> label="Select Language" name="translationLanguage">
                <Select labelInValue optionFilterProp="label" options={languagesForTranslations} showSearch />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
      </Col>
    </Row>
  );
};

export default UserTranslationsFormContent;
